import { useCallback, useEffect, useMemo, useState } from "react";
import { format } from "date-fns";
import { es } from "date-fns/locale";
import {
  AlarmClockCheck,
  CircleAlert,
  ClipboardList,
  LoaderCircle,
  MessageSquareText,
  RefreshCw
} from "lucide-react";

import {
  completeProfessionalTask,
  fetchProfessionalTasks,
  updateProfessionalTaskComment,
  type ProfessionalTask
} from "../lib/professionalTasksApi";
import "../styles/tareas.css";

type TaskFilter = "pending" | "done" | "all";

function formatDate(value?: string | null) {
  if (!value) {
    return "--";
  }

  const [year, month, day] = value.split("-").map(Number);
  const date = new Date(year, (month || 1) - 1, day || 1, 12, 0, 0, 0);
  if (Number.isNaN(date.getTime())) {
    return value;
  }

  return format(date, "d MMM yyyy", { locale: es });
}

function normalizeError(error: unknown, fallback: string) {
  if (typeof error === "object" && error && "response" in error) {
    const responseError = (error as { response?: { data?: { error?: string } } }).response?.data?.error;
    if (responseError) {
      return responseError;
    }
  }

  if (error instanceof Error && error.message.trim()) {
    return error.message;
  }

  return fallback;
}

function taskTypeLabel(task: ProfessionalTask) {
  const normalized = String(task.type || "").trim().toLowerCase();
  if (normalized.includes("recurrent")) {
    return `Recurrente · ${task.frecuency || "sin frecuencia"}`;
  }
  return "One shot";
}

function isExpired(task: ProfessionalTask) {
  if (Number(task.done) === 1 || !task.limit_date) {
    return false;
  }

  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return task.limit_date < today.toISOString().slice(0, 10);
}

export default function TareasPage() {
  const [tasks, setTasks] = useState<ProfessionalTask[]>([]);
  const [filter, setFilter] = useState<TaskFilter>("pending");
  const [loading, setLoading] = useState(true);
  const [savingTaskId, setSavingTaskId] = useState<number | null>(null);
  const [comments, setComments] = useState<Record<number, string>>({});
  const [error, setError] = useState<string | null>(null);

  const loadTasks = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const rows = await fetchProfessionalTasks();
      setTasks(rows);
    } catch (requestError) {
      setError(normalizeError(requestError, "No se pudieron cargar las tareas"));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void loadTasks();
  }, [loadTasks]);

  const summary = useMemo(() => {
    const pending = tasks.filter((task) => Number(task.done) !== 1);
    const done = tasks.filter((task) => Number(task.done) === 1);
    const expired = pending.filter(isExpired);

    return {
      pending: pending.length,
      done: done.length,
      expired: expired.length
    };
  }, [tasks]);

  const visibleTasks = useMemo(() => {
    if (filter === "pending") {
      return tasks.filter((task) => Number(task.done) !== 1);
    }
    if (filter === "done") {
      return tasks.filter((task) => Number(task.done) === 1);
    }
    return tasks;
  }, [filter, tasks]);

  async function handleComplete(task: ProfessionalTask) {
    const comment = String(comments[task.id] || "").trim();
    setSavingTaskId(task.id);
    setError(null);

    try {
      if (comment) {
        await updateProfessionalTaskComment(task.id, comment, false);
      }
      await completeProfessionalTask(task.id);
      setComments((prev) => ({ ...prev, [task.id]: "" }));
      await loadTasks();
    } catch (requestError) {
      setError(normalizeError(requestError, "No se pudo completar la tarea"));
    } finally {
      setSavingTaskId(null);
    }
  }

  return (
    <main className="shell-page">
      <section className="task-hero shell-hero-card">
        <div className="shell-hero-icon">
          <ClipboardList size={22} />
        </div>
        <div className="shell-hero-copy">
          <span className="shell-eyebrow">Seguimiento operativo</span>
          <h1>Tareas</h1>
          <p>Consulta tus tareas asignadas, deja contexto de cierre y marca cada accion como completada cuando quede resuelta.</p>
        </div>
      </section>

      <section className="task-stats-grid">
        <article className="shell-status-card">
          <span className="shell-card-label">Pendientes</span>
          <strong>{summary.pending}</strong>
          <p>Tareas aun por resolver en tu bandeja operativa.</p>
        </article>
        <article className="shell-status-card">
          <span className="shell-card-label">Completadas</span>
          <strong>{summary.done}</strong>
          <p>Tareas ya cerradas con confirmacion en el sistema.</p>
        </article>
        <article className="shell-status-card">
          <span className="shell-card-label">Vencidas</span>
          <strong>{summary.expired}</strong>
          <p>Prioridad alta. Son tareas pendientes cuya fecha limite ya paso.</p>
        </article>
      </section>

      <section className="task-panel">
        <div className="task-toolbar">
          <div className="task-filters">
            <button
              type="button"
              className={filter === "pending" ? "active" : ""}
              onClick={() => setFilter("pending")}
            >
              Pendientes
            </button>
            <button
              type="button"
              className={filter === "done" ? "active" : ""}
              onClick={() => setFilter("done")}
            >
              Completadas
            </button>
            <button
              type="button"
              className={filter === "all" ? "active" : ""}
              onClick={() => setFilter("all")}
            >
              Todas
            </button>
          </div>

          <button type="button" className="task-refresh-button" onClick={() => void loadTasks()}>
            <RefreshCw size={16} />
            Actualizar
          </button>
        </div>

        {error ? (
          <div className="task-error-box">
            <CircleAlert size={18} />
            <span>{error}</span>
          </div>
        ) : null}

        {loading ? (
          <div className="task-list">
            {Array.from({ length: 4 }).map((_, index) => (
              <div key={index} className="task-skeleton" />
            ))}
          </div>
        ) : visibleTasks.length === 0 ? (
          <div className="task-empty-state">No hay tareas en esta vista.</div>
        ) : (
          <div className="task-list">
            {visibleTasks.map((task) => {
              const done = Number(task.done) === 1;
              const expired = isExpired(task);

              return (
                <article
                  key={task.id}
                  className={[
                    "task-card",
                    done ? "is-done" : "",
                    expired ? "is-expired" : ""
                  ]
                    .filter(Boolean)
                    .join(" ")}
                >
                  <div className="task-card-top">
                    <div>
                      <div className="task-card-title">{task.description}</div>
                      <div className="task-card-meta">
                        <span>{taskTypeLabel(task)}</span>
                        <span>Limite {formatDate(task.limit_date)}</span>
                      </div>
                    </div>

                    <span className={`task-status-pill ${done ? "done" : expired ? "expired" : "pending"}`}>
                      {done ? "Completada" : expired ? "Vencida" : "Pendiente"}
                    </span>
                  </div>

                  {task.coach_comment ? (
                    <div className="task-comment-box">
                      <MessageSquareText size={15} />
                      <span>{task.coach_comment}</span>
                    </div>
                  ) : null}

                  {!done ? (
                    <div className="task-complete-box">
                      <textarea
                        value={comments[task.id] || ""}
                        onChange={(event) =>
                          setComments((prev) => ({
                            ...prev,
                            [task.id]: event.target.value
                          }))
                        }
                        placeholder="Anade un comentario de cierre si aporta contexto"
                      />

                      <button
                        type="button"
                        className="task-complete-button"
                        disabled={savingTaskId === task.id}
                        onClick={() => void handleComplete(task)}
                      >
                        {savingTaskId === task.id ? (
                          <>
                            <LoaderCircle size={16} className="spin" />
                            Guardando...
                          </>
                        ) : (
                          <>
                            <AlarmClockCheck size={16} />
                            Marcar completada
                          </>
                        )}
                      </button>
                    </div>
                  ) : null}
                </article>
              );
            })}
          </div>
        )}
      </section>
    </main>
  );
}
